import { CARD_TIMEOUT_MINUTES, consoleRoutes, WORKFLOW_BOOKING_ACTION_KIND, WORKFLOW_LEAD_ACTION_KIND } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { ShowBookingStep, ShowLeadFormStep, StepDuration } from "../types";
import { DurationField } from "./duration-field";
import { ExitsEditor } from "./exits-editor";
import type { FlowContext } from "./flow-context";
import { useRefs } from "./refs";
import { type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const DEFAULT_TIMEOUT: StepDuration = { value: CARD_TIMEOUT_MINUTES, unit: "minutes" };

export interface TimeoutProps {
  legend: string;
  hint: string;
  value: StepDuration | undefined;
  disabled: boolean;
  onChange: (after: StepDuration) => void;
}

/** How long the card stays open for the visitor; the default (30 minutes) shows until the author picks another. */
export function CardTimeout({ legend, hint, value, disabled, onChange }: TimeoutProps) {
  return (
    <div className="space-y-1.5">
      <DurationField legend={legend} duration={value ?? DEFAULT_TIMEOUT} disabled={disabled} onChange={onChange} />
      <p className="hint">{hint}</p>
    </div>
  );
}

interface ActionPickerProps extends Pick<FlowContext, "agentIds"> {
  kind: string;
  value: string | undefined;
  disabled: boolean;
  onChange: (id: string | undefined) => void;
}

/** The booking or lead action a card shows (limited to the workflow's agents when it has some). */
function ActionPicker({ kind, value, agentIds, disabled, onChange }: ActionPickerProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { actions, agents } = useRefs();
  const options = actions.filter((action) => action.kind === kind && (agentIds.length === 0 || agentIds.includes(action.agentId)));
  const manyAgents = new Set(options.map((option) => option.agentId)).size > 1;
  const agentId = agentIds[0] ?? agents[0]?.id;
  const known = !value || options.some((option) => option.id === value);
  const copy = kind === WORKFLOW_BOOKING_ACTION_KIND ? "booking" : "lead";
  return (
    <div className="space-y-2">
      <Field label={t(`${F}.${copy}Action`)} hint={t(`${F}.${copy}ActionHint`)}>
        <Select value={value ?? ""} disabled={disabled} onChange={(e) => { onChange(e.target.value || undefined); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {known ? null : <option value={value}>{t(`${F}.actionMissing`)}</option>}
          {options.map((option) => <option key={option.id} value={option.id}>{manyAgents ? `${option.name} · ${option.agentName}` : option.name}</option>)}
        </Select>
      </Field>
      {options.length === 0 && agentId ? (
        <p className="text-xs text-fg-muted">
          {t(`${F}.noActions`)}{" "}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.actions(summary.slug, agentId)}>{t(`${F}.openActions`)}</AppLink>
        </p>
      ) : null}
    </div>
  );
}

/** Show booking card: pick the booking action; how long the card stays open; what happens once the visitor books or leaves. */
export function ShowBookingForm({ step, disabled, onChange, paths, canAddPath, agentIds }: FormProps<ShowBookingStep> & FlowContext) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <ActionPicker kind={WORKFLOW_BOOKING_ACTION_KIND} value={step.action_id} agentIds={agentIds} disabled={disabled} onChange={(action_id) => { onChange({ ...step, action_id }); }} />
      <CardTimeout legend={t(`${F}.bookingTimeout`)} hint={t(`${F}.bookingTimeoutHint`)} value={step.not_booked_after} disabled={disabled} onChange={(not_booked_after) => { onChange({ ...step, not_booked_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Show lead form: pick the lead action; how long the form stays open; what happens once the visitor sends their details or leaves. */
export function ShowLeadFormForm({ step, disabled, onChange, paths, canAddPath, agentIds }: FormProps<ShowLeadFormStep> & FlowContext) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <ActionPicker kind={WORKFLOW_LEAD_ACTION_KIND} value={step.action_id} agentIds={agentIds} disabled={disabled} onChange={(action_id) => { onChange({ ...step, action_id }); }} />
      <CardTimeout legend={t(`${F}.leadTimeout`)} hint={t(`${F}.leadTimeoutHint`)} value={step.not_submitted_after} disabled={disabled} onChange={(not_submitted_after) => { onChange({ ...step, not_submitted_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}
